import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, LogOut, Settings, ChevronDown, MessageSquare, ShoppingCart, FileText, FlaskConical, Loader2, MapPin, Languages, Check, Palette, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AuthSession } from '@/types/auth';
import { ColorMode, getDashboardUrl, useColorMode, useLogoPath, useBrandName } from '@/components/theme-provider';

/** Auto-unpause presets (minutes) offered when pausing at peak hours — mirrors the backend enum. */
const REACTIVATE_PRESETS = [30, 60, 120, 180] as const;

/** Appearance options, in menu order. `SYSTEM` follows the OS preference. */
const COLOR_MODES = [
    { mode: ColorMode.LIGHT, icon: Sun, labelKey: 'header.appearance.light' },
    { mode: ColorMode.DARK, icon: Moon, labelKey: 'header.appearance.dark' },
    { mode: ColorMode.SYSTEM, icon: Monitor, labelKey: 'header.appearance.system' },
] as const;

interface AppHeaderProps {
    session: AuthSession | null;
    onLogout: () => Promise<void>;
    hasOrdersCapability: boolean;
    brandName?: string;
    phone?: string;
    aiAgentEnabled: boolean;
    aiAgentPaused: boolean;
    hasBrand: boolean;
    onToggleAiAgent: (
        paused: boolean,
        options?: { autoReplyMessage?: string; reactivateInMinutes?: number },
    ) => Promise<void>;
    pendingOrdersCount?: number;
    handoffCount?: number;
}

export function AppHeader({
    session,
    onLogout,
    hasOrdersCapability,
    brandName,
    phone,
    aiAgentEnabled,
    aiAgentPaused,
    hasBrand,
    onToggleAiAgent,
    pendingOrdersCount,
    handoffCount,
}: AppHeaderProps) {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const user = session?.user;
    const activeLanguage = i18n.resolvedLanguage ?? i18n.language;
    const { colorMode, setColorMode } = useColorMode();
    const dashboardUrl = getDashboardUrl();
    const logoPath = useLogoPath();
    const tenantLabel = useBrandName();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [toggling, setToggling] = useState(false);
    // Pause-dialog fields (both optional): heads-up message + auto-unpause preset.
    const [autoReplyMessage, setAutoReplyMessage] = useState('');
    const [reactivateInMinutes, setReactivateInMinutes] = useState<number | undefined>(undefined);

    const scope = session?.scope;
    // Line-less location-scoped sessions ride the shared master line — pausing
    // it would pause every sucursal, so the kill switch is owner-only.
    const showKillSwitch = hasBrand && aiAgentEnabled && (scope?.hasOwnLine ?? true);
    const currentPath = location.pathname;

    const handleToggleClick = () => {
        if (!aiAgentPaused) {
            setShowConfirmDialog(true);
        } else {
            handleConfirmToggle(false);
        }
    };

    const handleConfirmToggle = async (paused: boolean) => {
        setToggling(true);
        setShowConfirmDialog(false);
        try {
            await onToggleAiAgent(
                paused,
                paused
                    ? { autoReplyMessage: autoReplyMessage.trim() || undefined, reactivateInMinutes }
                    : undefined,
            );
            // Reset the dialog fields once applied so the next pause starts blank.
            setAutoReplyMessage('');
            setReactivateInMinutes(undefined);
        } finally {
            setToggling(false);
        }
    };

    const handleLogout = async () => {
        if (dashboardUrl) {
            window.location.href = `${dashboardUrl}/logout`;
        }
        await onLogout();
    };

    const navTab = (path: string, label: string, icon: React.ReactNode) => (
        <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(path)}
            aria-current={currentPath === path ? 'page' : undefined}
            className={`text-sm gap-1.5 px-2 sm:px-3 ${
                currentPath === path
                    ? 'text-foreground font-medium bg-muted'
                    : 'text-muted-foreground'
            }`}
        >
            {icon}
            <span className="hidden sm:inline">{label}</span>
        </Button>
    );

    return (
        <>
            <header className="h-14 bg-background border-b border-border flex items-center justify-between gap-2 px-2 sm:px-4 shrink-0">
                {/* Left: Logo + nav tabs */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <img src={logoPath} alt={tenantLabel} className="app-logo h-7 w-7 shrink-0" />
                    {(brandName || phone) && (
                        <div className="hidden sm:flex flex-col min-w-0">
                            {brandName && (
                                <span className="text-sm font-semibold text-foreground truncate max-w-[160px] leading-tight">
                                    {brandName}
                                </span>
                            )}
                            {phone && (
                                <span className="text-[11px] text-muted-foreground truncate max-w-[160px] leading-tight">
                                    {phone}
                                </span>
                            )}
                        </div>
                    )}
                    {scope?.locationId && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                            <MapPin className="h-3 w-3" />
                            {t('header.viewing', {
                                name: scope.locationName ?? t('header.locationFallback'),
                            })}
                        </span>
                    )}
                    <Separator orientation="vertical" className="h-6 hidden sm:block" />
                    <nav className="flex items-center gap-0.5 sm:gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            aria-current={currentPath === '/' ? 'page' : undefined}
                            className={`text-sm gap-1.5 px-2 sm:px-3 ${
                                currentPath === '/'
                                    ? 'text-foreground font-medium bg-muted'
                                    : 'text-muted-foreground'
                            }`}
                        >
                            <MessageSquare className="h-4 w-4" />
                            <span className="hidden sm:inline">{t('header.nav.chats')}</span>
                            {!!handoffCount && (
                                <span aria-live="polite" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                                    {handoffCount > 99 ? '99+' : handoffCount}
                                </span>
                            )}
                        </Button>
                        {hasOrdersCapability && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/orders')}
                                aria-current={currentPath === '/orders' ? 'page' : undefined}
                                className={`text-sm gap-1.5 px-2 sm:px-3 ${
                                    currentPath === '/orders'
                                        ? 'text-foreground font-medium bg-muted'
                                        : 'text-muted-foreground'
                                }`}
                            >
                                <ShoppingCart className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('header.nav.orders')}</span>
                                {!!pendingOrdersCount && currentPath !== '/orders' && (
                                    <span aria-live="polite" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-500 text-white text-[10px] font-bold leading-none">
                                        {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                                    </span>
                                )}
                            </Button>
                        )}
                        {navTab('/templates', t('header.nav.templates'), <FileText className="h-4 w-4" />)}
                        {navTab('/playground', t('header.nav.playground'), <FlaskConical className="h-4 w-4" />)}
                    </nav>
                </div>

                {/* Center/Right: Kill switch + controls */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    {showKillSwitch && (
                        <>
                            <button
                                onClick={handleToggleClick}
                                disabled={toggling}
                                className={`flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                    aiAgentPaused
                                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 animate-pulse'
                                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                                } ${toggling ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                            >
                                {toggling ? (
                                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                                ) : (
                                    <span
                                        className={`inline-block h-2 w-2 rounded-full ${
                                            aiAgentPaused ? 'bg-red-500' : 'bg-emerald-500'
                                        }`}
                                    />
                                )}
                                <span className="hidden min-[380px]:inline">
                                    {aiAgentPaused ? t('header.aiPaused') : t('header.aiActive')}
                                </span>
                            </button>
                            <Separator orientation="vertical" className="h-6 hidden sm:block" />
                        </>
                    )}

                    {dashboardUrl && (
                        <Button variant="ghost" size="sm" className="text-muted-foreground hidden sm:flex" asChild>
                            <a href={dashboardUrl}>
                                <LayoutDashboard className="h-4 w-4 mr-1.5" />
                                {t('header.goToDashboard')}
                            </a>
                        </Button>
                    )}

                    <DropdownMenu>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" aria-label={t('header.settings')} className="gap-1.5 px-2">
                                        <Settings className="h-5 w-5 text-muted-foreground" />
                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                    </Button>
                                </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent>{t('header.settings')}</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end" className="w-56">
                            {(user?.name || user?.email) && (
                                <>
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="text-left text-sm leading-tight">
                                            {user.name && <span className="block truncate font-semibold">{user.name}</span>}
                                            {user.email && (
                                                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                                            )}
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuGroup>
                                <DropdownMenuItem onClick={() => navigate('/settings')}>
                                    <Settings />
                                    {t('header.settings')}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                                <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    <Languages className="h-3.5 w-3.5" />
                                    {t('header.language.label')}
                                </DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => void i18n.changeLanguage('es')}>
                                    {t('header.language.spanish')}
                                    {activeLanguage === 'es' && <Check className="ml-auto h-4 w-4" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => void i18n.changeLanguage('en')}>
                                    {t('header.language.english')}
                                    {activeLanguage === 'en' && <Check className="ml-auto h-4 w-4" />}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                                <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    <Palette className="h-3.5 w-3.5" />
                                    {t('header.appearance.label')}
                                </DropdownMenuLabel>
                                {COLOR_MODES.map(({ mode, icon: Icon, labelKey }) => (
                                    <DropdownMenuItem key={mode} onClick={() => setColorMode(mode)}>
                                        <Icon />
                                        {t(labelKey)}
                                        {colorMode === mode && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            {dashboardUrl && (
                                <>
                                    <DropdownMenuGroup>
                                        <DropdownMenuItem asChild>
                                            <a href={dashboardUrl}>
                                                <LayoutDashboard />
                                                {t('header.goToDashboard')}
                                            </a>
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuItem onClick={handleLogout}>
                                <LogOut />
                                {t('header.logout')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* Confirmation dialog for pausing AI agent */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('header.pauseDialog.title')}</DialogTitle>
                        <DialogDescription>{t('header.pauseDialog.description')}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Optional heads-up sent to customers while paused. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="pause-auto-reply">{t('header.pauseDialog.autoReplyLabel')}</Label>
                            <Textarea
                                id="pause-auto-reply"
                                value={autoReplyMessage}
                                onChange={(e) => setAutoReplyMessage(e.target.value)}
                                placeholder={t('header.pauseDialog.autoReplyPlaceholder')}
                                rows={3}
                                maxLength={1000}
                            />
                        </div>

                        {/* Optional auto-unpause. "Manual" = stays off until turned on. */}
                        <div className="space-y-1.5">
                            <Label>{t('header.pauseDialog.reactivateLabel')}</Label>
                            <div className="flex flex-wrap gap-2">
                                {REACTIVATE_PRESETS.map((minutes) => (
                                    <Button
                                        key={minutes}
                                        type="button"
                                        size="sm"
                                        variant={reactivateInMinutes === minutes ? 'default' : 'outline'}
                                        onClick={() => setReactivateInMinutes(minutes)}
                                    >
                                        {t(`header.pauseDialog.reactivatePresets.${minutes}`)}
                                    </Button>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={reactivateInMinutes === undefined ? 'default' : 'outline'}
                                    onClick={() => setReactivateInMinutes(undefined)}
                                >
                                    {t('header.pauseDialog.reactivateManual')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            {t('header.pauseDialog.cancel')}
                        </Button>
                        <Button variant="destructive" onClick={() => handleConfirmToggle(true)}>
                            {t('header.pauseDialog.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
